import groupBy from 'lodash/groupBy';
import intersection from 'lodash/intersection';
import { ReferenceDataSchemaDefinition } from '../referenceData/ReferenceDataSchemaInterface';
import { BadInputError } from '@watershed/errors/BadInputError';
import { AnyError, WatershedError } from '@watershed/errors/WatershedError';
import { KwargType } from './types';
import { normalizeDate } from '../emissionsModel/utils';

export type RefDataBundle = {
  fileMetadataId: string;
  gcsPath: { bucketName: string; remotePath: string };
  schema?: ReferenceDataSchemaDefinition;
  referenceDataSourceId: string;
  referenceTableVersionId: string;
  referenceTableRevisionId: string;
};

export type EvaluationRefDataMetadata = RefDataBundle & {
  // A unique key to identify this object. May be used as a cache key.
  key: string;

  // Columns in the reference data to fetch.
  cols: Array<string>;
};

// For each column, store a map of unique values to the row indices where they
// can be found. The unique values are stringified using JSON.stringify.
export type InMemoryRefDataIndex = Record<
  string,
  Record<string, ReadonlyArray<number>>
>;

export type InMemoryRefData = {
  rows: ReadonlyArray<Record<string, any>>;

  index: InMemoryRefDataIndex;
};
export class NoResultsError extends WatershedError {
  // Loosely cribbed from UserInputError, but we can't extend that because our
  // subclasses provide more useful invariant method signatures
  status = 400;
  constructor(message?: string, cause?: AnyError) {
    super('BAD_USER_INPUT', message, cause);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BadInputError);
    }
    Object.setPrototypeOf(this, NoResultsError.prototype);
  }
}

// For turning any value from ref data into a string key to use in
// InMemoryRefData index.
function anyValueToIndexKey(v: any): string {
  return JSON.stringify(v);
}

export function createInMemoryRefDataIndex(
  rows: ReadonlyArray<Record<string, any>>,
  cols: ReadonlyArray<string>
): InMemoryRefDataIndex {
  const index: InMemoryRefDataIndex = {};
  for (const col of cols) {
    const valuesWithIndex: Array<[any, number]> = rows.map((row, i) => [
      row[col],
      i,
    ]);
    const groups: Record<string, Array<[any, number]>> = groupBy(
      valuesWithIndex,
      ([val, i]) => anyValueToIndexKey(val)
    );

    index[col] = {};
    for (const [k, pairs] of Object.entries(groups)) {
      index[col][k] = pairs.map((p) => p[1]);
    }
  }
  return index;
}

export type MatchOperation = KwargType | 'lessEqualDate' | 'greaterThanDate';
export type MatchArgs = Array<[string, any, MatchOperation]>;

// Returns a single matching row from refData, throwing an error if there are
// multiple matches.
export function findMatchingRow(
  refData: InMemoryRefData,
  args: Readonly<MatchArgs>
): Record<string, any> | null {
  // Special case for tables with only one row, and no args
  // since this is a common case for singletons
  if (args.length === 0 && refData.rows.length === 1) {
    return refData.rows[0];
  }

  let hasFuzzy = false;
  const predicates = Array<(row: Record<string, any>) => boolean>();

  const dateCheckers = Array<(row: Record<string, any>) => boolean>();

  // Find the overlapping set of rows based on column value.
  const indices: Array<ReadonlyArray<number>> = [];
  for (const [k, v, t] of args) {
    if (t === 'fuzzy') {
      hasFuzzy = true;
      const colIndex = refData.index[k];
      predicates.push((row) => {
        const indexKey = anyValueToIndexKey(row[k]);
        return colIndex[indexKey] != null;
      });
      indices.push(
        colIndex[anyValueToIndexKey(v)] ??
          colIndex[anyValueToIndexKey(null)] ??
          []
      );
    } else if (t === 'exact') {
      const colIndex = refData.index[k];
      indices.push(colIndex[anyValueToIndexKey(v)] ?? []);
    } else if (t === 'lessEqualDate') {
      dateCheckers.push((row) => {
        return row[k] === null || row[k] <= v;
      });
    } else if (t === 'greaterThanDate') {
      dateCheckers.push((row) => {
        return row[k] === null || row[k] > v;
      });
    }
  }

  const overlappingIndices: Array<number> = intersection(...indices);

  if (hasFuzzy) {
    overlappingIndices.sort((a, b) => {
      const aRow = refData.rows[a];
      const bRow = refData.rows[b];
      return predicates.reverse().reduce((score, predicate, i) => {
        return (
          score +
          (predicate(aRow) ? 2 ** i : 0) -
          (predicate(bRow) ? 2 ** i : 0)
        );
      }, 0);
    });
  } else if (overlappingIndices.length === 0) {
    throw new NoResultsError(
      `0 rows found that matched, but expected exactly one. Args: ${JSON.stringify(
        args
      )}`
    );
  } else if (overlappingIndices.length > 1) {
    const matchingRows = overlappingIndices.map((index) => refData.rows[index]);

    // use the date checkers to filter out rows that don't match if possible
    if (dateCheckers.length > 0) {
      const filteredRows = matchingRows.filter((row) => {
        return dateCheckers.every((checker) => checker(row));
      });
      if (filteredRows.length === 1) {
        return filteredRows[0];
      }
    }

    throw new NoResultsError(
      `${
        overlappingIndices.length
      } rows found that matched, but expected exactly one. Args: ${JSON.stringify(
        args
      )} Matches: ${JSON.stringify(matchingRows)}.`
    );
  }

  return refData.rows[overlappingIndices[0]];
}

// Returns all matching rows from refData based on the provided arguments
export function findAllMatchingRows(
  refData: InMemoryRefData,
  args: Readonly<MatchArgs>
): Array<Record<string, any>> {
  // Special case for tables with only one row, and no args
  // since this is a common case for singletons
  if (args.length === 0) {
    return [...refData.rows];
  }

  let hasFuzzy = false;
  const predicates = Array<(row: Record<string, any>) => boolean>();

  const dateCheckers = Array<(row: Record<string, any>) => boolean>();
  let hasOnlyDateFilters = true;

  // Find the overlapping set of rows based on column value.
  const indices: Array<ReadonlyArray<number>> = [];
  for (const [k, v, t] of args) {
    if (t === 'fuzzy') {
      hasOnlyDateFilters = false;
      hasFuzzy = true;
      const colIndex = refData.index[k];
      predicates.push((row) => {
        const indexKey = anyValueToIndexKey(row[k]);
        return colIndex[indexKey] != null;
      });
      indices.push(
        colIndex[anyValueToIndexKey(v)] ??
          colIndex[anyValueToIndexKey(null)] ??
          []
      );
    } else if (t === 'exact') {
      hasOnlyDateFilters = false;
      const colIndex = refData.index[k];
      indices.push(colIndex[anyValueToIndexKey(v)] ?? []);
    } else if (t === 'lessEqualDate') {
      dateCheckers.push((row) => {
        const row_val = normalizeDate(row[k]);
        return row_val === null || row_val <= v;
      });
    } else if (t === 'greaterThanDate') {
      dateCheckers.push((row) => {
        const row_val = normalizeDate(row[k]);
        return row_val === null || row_val > v;
      });
    }
  }

  // If we only have date filters, apply them to all rows
  if (hasOnlyDateFilters && dateCheckers.length > 0) {
    return refData.rows.filter((row) => {
      return dateCheckers.every((checker) => checker(row));
    });
  }

  const overlappingIndices: Array<number> = intersection(...indices);

  if (hasFuzzy) {
    overlappingIndices.sort((a, b) => {
      const aRow = refData.rows[a];
      const bRow = refData.rows[b];
      return predicates.reverse().reduce((score, predicate, i) => {
        return (
          score +
          (predicate(aRow) ? 2 ** i : 0) -
          (predicate(bRow) ? 2 ** i : 0)
        );
      }, 0);
    });
  }

  if (overlappingIndices.length === 0) {
    return [];
  }

  let matchingRows = overlappingIndices.map((index) => refData.rows[index]);

  // Apply date checkers if any
  if (dateCheckers.length > 0) {
    matchingRows = matchingRows.filter((row) => {
      return dateCheckers.every((checker) => checker(row));
    });
  }

  return matchingRows;
}
